import React, { FC } from 'react'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { Page } from '@moonpig/web-core-app'
import { Routes } from '@moonpig/web-core-routing'
import Head from 'next/head'
import { Region } from '@moonpig/web-core-types'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { DEPARTMENT_PATHS } from '@moonpig/web-core-brand/config'
import {
  FavouritesProvider,
  isCardProduct,
  PageTypeEnum,
} from '@moonpig/web-shared-products'
import { useFlag } from '@moonpig/web-explore-flags'
import { buildSearchTitle } from '../../utils/buildSearchTitle'
import { capitaliseFirstCharacter } from '../../utils'
import { SearchDetails } from '../../components/types'
import { DYNAMIC_PAGE_TYPE, GALLERY_PAGE_SIZE } from '../../constants'
import { useFindLocaleText } from '../../text-localisation'
import { getHeadLinksTags } from '../../utils/gallery/galleryPagination'
import { SearchPage } from '../SearchPage/SearchPage'

type DynamicPageProps = React.ComponentProps<typeof SearchPage>

type SearchHeadProps = {
  details: SearchDetails
  headLinkTags: {
    canonical: string | null
    next: string | null
    prev: string | null
  } | null
  isIndexed?: boolean
  region: Region
  page: number
}

const getCutOffMetaDescriptionId = (
  department: DepartmentsEnum,
  region: Region,
) => {
  const parentDepartment = getParentDepartment(department)

  switch (parentDepartment) {
    case DepartmentsEnum.ALL_GIFTS: {
      return 'find.dynamic_page_gift_cut_off'
    }

    case DepartmentsEnum.ALL_FLOWERS_AND_PLANTS: {
      return 'find.dynamic_page_flower_cut_off'
    }
    default: {
      return region === 'uk'
        ? 'find.dynamic_page_uk_card_cut_off'
        : 'find.dynamic_page_card_cut_off'
    }
  }
}

const capitaliseFirstLetters = (searchTerm: string): string => {
  return searchTerm.split(' ').map(capitaliseFirstCharacter).join(' ')
}

const shouldBeIndexed = async (path: string): Promise<boolean> => {
  try {
    const stringToCheck = `${path}</loc>`

    const sitemapUrl = `${process.env.HOST_URL}/.well-known/d-sitemap.xml`

    const timeStamp = Date.now()
    const response = await fetch(`${sitemapUrl}?nocache="${timeStamp}"`)

    const string = await response.text()
    return string.includes(stringToCheck)
  } catch {
    return false
  }
}

const DynamicHead: FC<SearchHeadProps> = ({
  details,
  headLinkTags,
  isIndexed,
  region,
  page,
}) => {
  const { searchTerm } = details
  const title = capitaliseFirstLetters(searchTerm)
  const currentDepartment = details.department[0]
  const robotsMetaContent = isIndexed ? 'index, follow' : 'noindex, nofollow'
  const localiseText = useFindLocaleText()
  const metaDescriptionId = getCutOffMetaDescriptionId(
    currentDepartment,
    region,
  )
  const metaDescription = `${title}. ${localiseText(metaDescriptionId)}`

  const dynamicTitle = buildSearchTitle(title, page, localiseText('find.page'))

  return (
    headLinkTags && (
      <Head>
        <title>{dynamicTitle}</title>
        <meta name="title" content={dynamicTitle} />
        {headLinkTags.canonical && (
          <link
            rel="canonical"
            key="content-head-canonical"
            href={headLinkTags.canonical}
          />
        )}
        {headLinkTags.prev && <link rel="prev" href={headLinkTags.prev} />}
        {headLinkTags.next && <link rel="next" href={headLinkTags.next} />}
        {headLinkTags.canonical && (
          <meta property="og:url" content={headLinkTags.canonical} />
        )}
        {(region === 'uk' || isCardProduct(currentDepartment)) && (
          <>
            <meta name="description" content={metaDescription} />
            <meta property="og:title" content={metaDescription} />
            <meta property="og:description" content={metaDescription} />
          </>
        )}
        <meta property="og:locale" content="en-GB" />
        <meta property="og:type" content="website" />
        <meta data-testid="robots" name="robots" content={robotsMetaContent} />
        {page > 1 && (
          <>
            <meta data-testid="bingbot" name="bingbot" content="noindex" />
            <meta
              data-testid="googlebot"
              name="googlebot"
              content="noindex,follow"
            />
          </>
        )}
      </Head>
    )
  )
}

export const DynamicPage: Page<DynamicPageProps, Routes['dynamic']> = props => {
  const {
    details,
    environment,
    page,
    initialResponse: { totalCount: count },
    region,
    isIndexed,
  } = props

  const pageTitle = capitaliseFirstLetters(details.searchTerm)

  const headLinkTags = getHeadLinksTags({
    environment,
    path: details?.url,
    offset: (page - 1) * GALLERY_PAGE_SIZE,
    count,
  })
  const head = (
    <DynamicHead
      details={details}
      headLinkTags={headLinkTags}
      isIndexed={isIndexed}
      region={region}
      page={page}
    />
  )

  return (
    <FavouritesProvider
      location={pageTitle}
      enableToastNotification={useFlag('enable-fav-toast-notification')}
    >
      <SearchPage
        {...(props as React.ComponentProps<typeof SearchPage>)}
        headOverride={head}
        useClientSideRouting={false}
        pageType={PageTypeEnum.DYNAMIC}
        pageTitle={pageTitle}
      />
    </FavouritesProvider>
  )
}

DynamicPage.layout = 'default'

DynamicPage.getInitialProps = async context => {
  const pathParts = context.query?.parts

  const dynamicDepartmentPath = pathParts && pathParts[0]
  const dynamicSearchTerm = pathParts && pathParts[1]?.replace(/-/g, ' ')
  const dynamicDepartment: DepartmentsEnum[] = []

  if (dynamicSearchTerm) {
    switch (dynamicDepartmentPath) {
      case DEPARTMENT_PATHS.cards:
        dynamicDepartment.push(DepartmentsEnum.ALL_CARDS)
        break
      case DEPARTMENT_PATHS.gifts:
        dynamicDepartment.push(DepartmentsEnum.ALL_GIFTS)
        break
      case DEPARTMENT_PATHS['flowers-and-plants']:
        dynamicDepartment.push(DepartmentsEnum.ALL_FLOWERS_AND_PLANTS)
        break
    }
  }

  const isIndexed = await shouldBeIndexed(context.path)

  const modifiedContext = {
    ...context,
    dynamicSearchTerm,
    dynamicDepartment,
    pageType: DYNAMIC_PAGE_TYPE,
    luxLabel: 'Dynamic Page',
    pageTitle: capitaliseFirstLetters(dynamicSearchTerm || ''),
    isIndexed,
  }

  // eslint-disable-next-line no-return-await
  return await SearchPage.getInitialProps(modifiedContext)
}
